import { useNavigate } from 'react-router-dom';
import { ACTION_SEQUENCE_STEP_TYPE } from '../../steptypes';
import { useSelector } from 'react-redux';
import { getStepFieldValue } from '../utils';
import { parse } from '../../../parse';

const usePortalNavigate = () => {
  const navigate = useNavigate();

  const user = useSelector(state => state.User);
  const { dataLinks } = useSelector(state => state.DataLinks);

  const navigateToRoute = async ({
    history,
    data,
    step,
    documents,
    selections,
    local,
    params
  }) => {
    let url = parse({
      markup: step.navigationRoute,
      actionSequenceExecutionHistory: history,
      documents,
      selections,
      local,
      user,
      data,
      dataLinks,
      params
    });

    if (user && url.indexOf('{') > -1) {
      url = parse({
        markup: url,
        document: user,
        actionSequenceExecutionHistory: history,
        documents,
        selections,
        local,
        user,
        data,
        dataLinks,
        params
      });
    }

    if (step.querystring) {
      const querystring = parse({
        markup: step.querystring,
        actionSequenceExecutionHistory: history,
        documents,
        selections,
        local,
        user,
        dataLinks,
        data,
        params
      });

      if (querystring) {
        url += querystring.indexOf('?') === 0 ? querystring : `?${querystring}`;
      }
    }

    navigate(url);
    return {
      success: true,
      message: `navigated to route: ${url}`
    };
  };

  const navigateToPage = async ({
    history,
    data,
    step,
    documents,
    selections,
    local,
    params
  }) => {
    // replace parameters in the path
    let path = step.navigationPagePath;

    step.fields?.forEach(stepField => {
      const fieldValue = getStepFieldValue({
        stepField,
        underlyingField: {
          name: stepField.dynamicFieldName || stepField.name
        },
        selections,
        history,
        documents,
        user,
        local,
        dataLinks,
        data,
        params
      });

      path = path.replace(
        `:${stepField.dynamicFieldName?.replace(':', '') || stepField.name}`,
        fieldValue
      );
    });

    if (step.querystring) {
      const querystring = parse({
        markup: step.querystring,
        actionSequenceExecutionHistory: history,
        documents,
        selections,
        local,
        user,
        dataLinks,
        data,
        params
      });

      if (querystring) {
        path +=
          querystring.indexOf('?') === 0 ? querystring : `?${querystring}`;
      }
    }

    navigate(path);
    return {
      success: true,
      message: `navigated to page: ${path}`
    };
  };

  const navigateToUrl = async ({
    history,
    step,
    documents,
    selections,
    local,
    params,
    data
  }) => {
    // have we come straight from actionSequence.run?
    if (step.navigationUrlTarget) {
      window.open(step.navigationUrl, step.navigationUrlTarget);
      return {
        success: true,
        message: `navigated to url: ${step.navigationUrl} in ${step.navigationUrlTarget}`
      };
    } else if (step.navigationUrl) {
      window.location.href = step.navigationUrl;
      return {
        success: true,
        message: `navigated to url: ${step.navigationUrl}`
      };
    }

    let url = step.urlValue;
    if (step.urlSource === 'datalink') {
      if (selections[step.urlDataLinkId]) {
        const document = documents[step.urlDataLinkId]?.documents.find(
          d => d.id === selections[step.urlDataLinkId].documentId
        );
        if (document) {
          if (Array.isArray(document[step.urlDataLinkField])) {
            if (document[step.urlDataLinkField]?.length > 0) {
              url = document[step.urlDataLinkField][0].original;
            }
          } else {
            url = document[step.urlDataLinkField];
          }
        }
      }
    }

    if (!url) {
      return {
        error: 'url is undefined'
      };
    }

    url = parse({
      markup: url,
      actionSequenceExecutionHistory: history,
      documents,
      selections,
      local,
      user,
      dataLinks,
      data,
      params
    });

    if (user && url.indexOf('{') > -1) {
      url = parse({
        markup: url,
        document: user,
        actionSequenceExecutionHistory: history,
        documents,
        selections,
        local,
        user,
        dataLinks,
        data,
        params
      });
    }

    if (step.querystring) {
      const querystring = parse({
        markup: step.querystring,
        actionSequenceExecutionHistory: history,
        documents,
        selections,
        local,
        user,
        dataLinks,
        data,
        params
      });

      if (querystring) {
        url += querystring.indexOf('?') === 0 ? querystring : `?${querystring}`;
      }
    }

    if (step.urlOpenInNewTab) {
      window.open(url, '_blank');
      return {
        success: true,
        message: `navigated to url: ${url} in new tab`
      };
    } else {
      window.location.href = url;
      return { success: true, message: `navigated to url: ${url}` };
    }
  };

  const navigateBackwards = async () => {
    navigate(-1);
    return { success: true };
  };

  const executeNavigationStep = async step => {
    switch (step.type) {
      case ACTION_SEQUENCE_STEP_TYPE.NAVIGATE_BACK: {
        navigateBackwards();
        break;
      }
      case ACTION_SEQUENCE_STEP_TYPE.NAVIGATE_TO_ROUTE: {
        navigateToRoute(step);
        break;
      }
      case ACTION_SEQUENCE_STEP_TYPE.NAVIGATE_TO_PAGE: {
        navigateToPage(step);
        break;
      }
      case ACTION_SEQUENCE_STEP_TYPE.NAVIGATE_TO_URL: {
        navigateToUrl(step);
        break;
      }
      default:
        break;
    }
  };

  return {
    navigateToRoute,
    navigateToUrl,
    navigateToPage,
    navigateBackwards,
    executeNavigationStep
  };
};

export default usePortalNavigate;
